import { useEffect, useState } from "react";
import { theme, card, type as text } from "../theme";
import { api } from "../api/client";
import { useExperience } from "../context/ExperienceContext";
import { buildInsightsMap } from "../components/ControlInsightsPanel";
import type { ControlInsights } from "../components/ControlInsightsPanel";
import { FrameworkControlList, StandardListState } from "../components/FrameworkControlList";
import type { SoaRow, EvidenceDoc } from "../components/FrameworkControlList";
import { StandardHeader, STANDARD_PAGE_LABELS } from "../components/standards/StandardHeader";
import { frameworkAccent } from "../components/controlStatus";
import { IndexTabs } from "../components/Drawing";

// SOC 2: the AICPA Trust Services Criteria. Reference tabs (overview, scoping, common audit gaps)
// around a self-assessment on the shared assessment list (components/FrameworkControlList.tsx),
// grouped by criteria series (CC1 to CC9, A1, C1). The report type only changes the words the
// assessment uses; it is not stored.

const TRUST_PRINCIPLES = [
  {
    code: "Security",
    required: true,
    criteria: "CC1–CC9",
    description:
      "The system is protected against unauthorised access (both logical and physical). Security is the only Trust Services category required in every SOC 2 report. All Common Criteria (CC1–CC9) apply and are reused as the foundation for every other category.",
    trigger: "Always included",
  },
  {
    code: "Availability",
    required: false,
    criteria: "A1.1–A1.3",
    description:
      "The system is available for operation and use as committed or agreed. Covers capacity planning, backup and recovery infrastructure, and tested disaster-recovery procedures. Written DR plans alone are insufficient — A1.3 requires evidence that recovery has actually been tested.",
    trigger: "When you sell uptime SLAs or public availability commitments",
  },
  {
    code: "Processing Integrity",
    required: false,
    criteria: "PI1.1–PI1.5",
    description:
      "System processing is complete, valid, accurate, timely, and authorised. Covers inputs, processing, outputs, and stored data. The rarest category in practice — applies to payments, billing engines, trading systems, analytics platforms where processing accuracy is core to the product value.",
    trigger: "When customers rely on your processing accuracy for business decisions",
  },
  {
    code: "Confidentiality",
    required: false,
    criteria: "C1.1–C1.2",
    description:
      "Information designated as confidential is protected as committed or agreed. Covers data classification, handling, and verified disposal. Distinct from Privacy — Confidentiality covers business-designated confidential information (trade secrets, contracts, IP, financials), not personal data specifically.",
    trigger: "When you handle business-designated confidential information under NDA or contract",
  },
  {
    code: "Privacy",
    required: false,
    criteria: "P1.1–P8.1",
    description:
      "Personal information is collected, used, retained, disclosed, and disposed of in conformity with the entity's privacy notice and with GAPP criteria. Applies when the organisation is a data controller with direct data-subject relationships. If you only process PII on a customer's behalf without interacting with data subjects, Confidentiality controls are often sufficient.",
    trigger: "When you directly collect or control personal information from data subjects",
  },
];

const REPORT_SECTIONS = [
  {
    num: "1",
    title: "Management's description",
    desc: "Describes the system boundaries, components (infrastructure, software, people, procedures, data), and how controls are designed to meet the applicable Trust Services Criteria. Written by management.",
  },
  {
    num: "2",
    title: "Management's written assertion",
    desc: "Formal statement by management that the description is fairly presented and that controls were suitably designed (Type 1 & 2) and operated effectively (Type 2 only). Includes assertion on whether criteria were met.",
  },
  {
    num: "3",
    title: "Auditor's tests and results",
    desc: "The CPA firm's documented tests of each in-scope criterion and the results of testing. Deviations are reported here. This is the largest section of a Type 2 report and the primary source of customer assurance.",
  },
  {
    num: "4",
    title: "Auditor's opinion",
    desc: "Independent opinion on whether the description is fairly presented and controls are suitably designed / operating effectively. Opinion is unqualified (clean) if no material deviations were found, or qualified if material deviations exist.",
  },
];

const RISK_STEPS = [
  {
    step: "1",
    title: "Identify services in scope",
    desc: "What systems and processes are in scope? Which customer commitments (uptime SLAs, NDA terms, data processing agreements, privacy notices) are relevant?",
  },
  {
    step: "2",
    title: "Evaluate user-entity risk",
    desc: "From the customer's perspective, what risks would they face if the service organisation's controls were inadequate? This drives which Trust Services categories to include.",
  },
  {
    step: "3",
    title: "Select applicable Trust Services criteria",
    desc: "Security is mandatory. Availability, Processing Integrity, Confidentiality, and Privacy are added based on customer commitments and regulatory exposure.",
  },
  {
    step: "4",
    title: "Map controls to criteria",
    desc: "For each in-scope criterion, identify the control(s) the organisation relies upon to satisfy it; document evidence retention requirements before the audit window opens.",
  },
  {
    step: "5",
    title: "Identify gaps and CUECs",
    desc: "Identify criteria that cannot be fully met by the service organisation alone. Document Complementary User Entity Controls (CUECs) — controls customers must implement — and disclose them in the report.",
  },
];

const SUBSERVICE_METHODS = [
  {
    method: "Carve-out (most common)",
    desc: "The subservice organisation's controls are excluded from scope. The report describes what controls the service organisation expects the subservice org to operate, without testing them. Customers must obtain the subservice org's own SOC 2 report to achieve full assurance.",
    when: "When the subservice organisation has its own SOC 2 or when the control boundary is clean and well-understood.",
  },
  {
    method: "Inclusive (rare)",
    desc: "The subservice organisation's controls are included in scope. The service auditor obtains evidence from and describes the subservice org's controls alongside the service organisation's. Requires the subservice org's cooperation and access.",
    when: "When customers strongly demand it or the subservice org has no independent audit report.",
  },
];

const EVIDENCE_GAPS = [
  { area: "CC6.2 / CC6.3 — Access reviews", gap: "Reviews happen informally with no dated artefact, no record of who approved continued access, and no documentation of resulting changes. The most frequently cited gap in SOC 2 audits." },
  { area: "A1.3 — Recovery testing", gap: "Written DR/recovery plans exist but recovery has never been tested, or tests ran without producing documented results. A1.3 explicitly requires evidence of actual testing." },
  { area: "CC7 — Incident response", gap: "An IR plan exists but has never been tested or was tested without documented results. Tabletop exercises must produce written records." },
  { area: "C1.2 — Confidential data disposal", gap: "Encryption is demonstrable but verified deletion is not. Organisations can encrypt; they cannot prove data was destroyed. Typical remedy: automated lifecycle policies with deletion audit logs." },
  { area: "CC8.1 — Change management", gap: "Sophisticated CI/CD pipelines with no documented approval process, or emergency changes that bypassed controls without retroactive review." },
  { area: "CC1 / CC5 — Generic policies", gap: "Policy documents exist but are templated and do not reflect actual operations. Auditors compare policy language against observed practice and flag gaps." },
  { area: "PI1.4 — Output completeness", gap: "Missing end-to-end reconciliation evidence for Processing Integrity engagements." },
  { area: "CC9.2 — Vendor and AI risk", gap: "Third-party and AI-vendor risks introduced but not periodically reassessed. A common emerging gap as AI-dependent services proliferate." },
];


// ─── Component ───────────────────────────────────────────────────────────────

type Section = "overview" | "assessment" | "process" | "gaps";

const SECTIONS: Array<{ key: Section; label: string }> = [
  { key: "overview", label: "Overview" },
  { key: "assessment", label: "Self-assessment" },
  { key: "process", label: "Scoping and report structure" },
  { key: "gaps", label: "Common audit gaps" },
];

const FUNCTIONAL_AREAS = [
  { area: "Policies", desc: "Documented objectives and policies exist for each Trust Services category in scope. Evidence: approved policy documents, version history, acknowledgments." },
  { area: "Communication", desc: "Policies and responsibilities communicated to relevant parties: employees, users, vendors. Evidence: training records, policy distribution logs, trust centre." },
  { area: "Procedures", desc: "Controls must actually operate, not just exist as documentation. Evidence: system logs, dated screenshots, access-review records, ticket history." },
  { area: "Monitoring", desc: "System monitored; action taken to maintain compliance. Evidence: pen-test results, SIEM dashboards, incident post-mortems, audit reports." },
];

const DEVIATION_TYPES = [
  { name: "Design deviation", scope: "Type 1 and Type 2", desc: "A control was not designed in a manner sufficient to meet the criterion, even if it operated exactly as designed." },
  { name: "Operating effectiveness deviation", scope: "Type 2 only", desc: "A control was suitably designed but did not consistently operate as intended during the review period." },
  { name: "Compliance deviation", scope: "Privacy reports (P-series)", desc: "Personal information was collected, used, retained, or disclosed in a manner inconsistent with the entity's privacy commitments or GAPP criteria." },
];

export function Soc2Page() {
  const { novice } = useExperience();
  const accent = frameworkAccent("soc2");
  const [activePrinciple, setActivePrinciple] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<Section>("overview");
  const [insightsMap, setInsightsMap] = useState<Map<string, ControlInsights>>(new Map());
  const [soaRows, setSoaRows] = useState<SoaRow[]>([]);
  const [docs, setDocs] = useState<EvidenceDoc[]>([]);
  const [soaLoaded, setSoaLoaded] = useState(false);
  const [soaError, setSoaError] = useState<string | null>(null);
  const [reportType, setReportType] = useState<1 | 2>(2);

  useEffect(() => {
    api.get<{ run: { resultJson: unknown } | null }>("/guidance/latest")
      .then((d) => { if (d.run?.resultJson) setInsightsMap(buildInsightsMap(d.run.resultJson)); })
      .catch(() => {});
  }, []);

  async function loadAll() {
    try {
      const [d, docsData] = await Promise.all([
        api.get<{ soa: SoaRow[] }>("/controls/soa"),
        api.get<{ documents: EvidenceDoc[] }>("/documents").catch(() => ({ documents: [] as EvidenceDoc[] })),
      ]);
      setSoaRows(d.soa.filter((r) => r.control.frameworkId === "soc2" || r.control.id.startsWith("soc2-")));
      setDocs(docsData.documents);
      setSoaError(null);
    } catch (e) {
      setSoaError(e instanceof Error ? e.message : "Failed to load criteria");
    } finally {
      setSoaLoaded(true);
    }
  }

  useEffect(() => {
    if (activeSection !== "assessment" || soaLoaded) return;
    void loadAll();
  }, [activeSection, soaLoaded]);

  const heading: React.CSSProperties = { ...text.sectionTitle, marginBottom: 12 };
  const accentCard: React.CSSProperties = { ...card, borderLeft: `4px solid ${accent}` };

  return (
    <div className="page-pad">
      <StandardHeader
        frameworkId="soc2"
        page={STANDARD_PAGE_LABELS.criteria}
        subtitle="The AICPA attestation that your systems meet the Trust Services Criteria you select, evidenced by an independent CPA firm's audit."
      >
        <p style={{ ...text.meta, margin: "6px 0 0" }}>AI insights appear on a criterion once a compliance check has been run.</p>
      </StandardHeader>

      {/* Type I / Type II */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        {[
          { label: "SOC 2 Type I", badge: "Point in time", desc: "Controls suitably designed as of a specific date" },
          { label: "SOC 2 Type II", badge: "Period", desc: "Controls suitably designed and operating effectively over 6 to 12 months" },
        ].map((r) => (
          <div key={r.label} style={{ ...accentCard, flex: "1 1 280px", minWidth: 0 }}>
            <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4 }}>
              <span style={{ ...text.body, fontWeight: 700, color: theme.color.heading }}>{r.label}</span>
              <span style={{ ...text.meta, fontWeight: 700, padding: "1px 6px", borderRadius: 3, background: theme.color.panelAlt, border: `1px solid ${theme.color.border}` }}>{r.badge}</span>
            </div>
            <div style={text.hint}>{r.desc}</div>
          </div>
        ))}
      </div>

      <IndexTabs
        items={SECTIONS.map((s) => ({ id: s.key, label: s.label }))}
        value={activeSection}
        onChange={setActiveSection}
        style={{ marginBottom: 20 }}
      />

      {/* ── Overview ─────────────────────────────────────────────────────── */}
      {activeSection === "overview" && (
        <div>
          <h2 style={heading}>The five Trust Services categories</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 10, marginBottom: 24 }}>
            {TRUST_PRINCIPLES.map((p) => {
              const isActive = activePrinciple === p.code;
              return (
                <button
                  key={p.code}
                  type="button"
                  onClick={() => setActivePrinciple(isActive ? null : p.code)}
                  style={{ ...accentCard, cursor: "pointer", textAlign: "left", background: isActive ? theme.color.panelAlt : theme.color.panel }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <span style={{ ...text.body, fontWeight: 700, color: theme.color.heading }}>{p.code}</span>
                    {p.required && (
                      <span style={{ ...text.meta, fontWeight: 700, padding: "1px 6px", borderRadius: 3, background: theme.color.successBg, color: theme.color.successText, border: `1px solid ${theme.color.success}` }}>Required</span>
                    )}
                    <span style={{ ...text.ref, fontSize: text.meta.fontSize, marginLeft: "auto", color: theme.color.faint }}>{p.criteria}</span>
                  </div>
                  <div style={text.meta}>{p.trigger}</div>
                  {isActive && (
                    <div style={{ ...text.hint, marginTop: 8, paddingTop: 8, borderTop: `1px solid ${theme.color.border}` }}>{p.description}</div>
                  )}
                </button>
              );
            })}
          </div>

          <h2 style={heading}>Subservice organisation methods</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 10 }}>
            {SUBSERVICE_METHODS.map((m) => (
              <div key={m.method} style={accentCard}>
                <div style={{ ...text.body, fontWeight: 700, color: theme.color.heading, marginBottom: 6 }}>{m.method}</div>
                <div style={{ ...text.hint, marginBottom: 8 }}>{m.desc}</div>
                <div style={{ ...text.meta, fontStyle: "italic" }}>Use when: {m.when}</div>
              </div>
            ))}
          </div>

          <div style={{ ...accentCard, marginTop: 16 }}>
            <div style={{ ...text.body, fontWeight: 700, color: theme.color.heading, marginBottom: 6 }}>Complementary user entity controls (CUECs)</div>
            <div style={text.hint}>
              CUECs are controls the service organisation assumes customers implement to make the combined system of controls effective. They are disclosed in Section 1 of the SOC 2 report but the auditor does not test them. Customers must read CUECs carefully during vendor due diligence: they define responsibilities that sit with the customer, not the vendor. Common examples: managing own user access permissions, configuring MFA for their accounts, acting on security notifications, and backing up their own data when backup is not included in the service.
            </div>
          </div>
        </div>
      )}

      {/* ── Self-assessment ──────────────────────────────────────────────── */}
      {activeSection === "assessment" && (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
            <span style={text.label}>Assessing for</span>
            <select
              aria-label="Report type"
              value={reportType}
              onChange={(e) => setReportType(Number(e.target.value) as 1 | 2)}
              style={{ padding: "6px 10px", borderRadius: theme.radius.sm, border: `1px solid ${theme.color.borderStrong}`, background: theme.color.panel, color: theme.color.text, fontSize: text.hint.fontSize }}
            >
              <option value={1}>Type I: design suitability</option>
              <option value={2}>Type II: operating effectiveness</option>
            </select>
          </div>
          <div style={{ ...accentCard, marginBottom: 16 }}>
            <div style={{ ...text.eyebrow, marginBottom: 4 }}>{reportType === 1 ? "Type I: design suitability" : "Type II: operating effectiveness"}</div>
            <div style={text.hint}>
              {reportType === 1
                ? "For each criterion below, assess whether your control is designed to meet the criterion as of your report date. Collect policies, architecture diagrams, configuration records, and procedure documentation that demonstrate how the control is designed to operate."
                : "For each criterion below, assess whether your control operated consistently throughout the audit period. You need population-based samples, dated logs, approval records, access-review artefacts, and monitoring evidence covering the full 6 to 12 month window. Design documentation alone is insufficient for Type II."}
            </div>
          </div>

          {soaLoaded && soaError ? (
            <div style={{ ...card, borderLeft: `4px solid ${theme.color.danger}`, padding: "14px 16px" }}>
              <div style={{ ...text.itemTitle, marginBottom: 4 }}>Could not load criteria</div>
              <div style={text.hint}>{soaError}</div>
              <button type="button" onClick={() => { setSoaLoaded(false); setSoaError(null); }} style={{ marginTop: 10, ...text.meta, padding: "4px 12px", borderRadius: 3, cursor: "pointer", border: `1px solid ${theme.color.border}`, background: theme.color.panel }}>
                Retry
              </button>
            </div>
          ) : (
            <>
              <StandardListState loading={!soaLoaded} empty={soaLoaded && soaRows.length === 0} emptyTitle="No SOC 2 criteria found">
                Run the database seed to populate the Trust Services Criteria.
              </StandardListState>
              {soaLoaded && soaRows.length > 0 && (
                <FrameworkControlList
                  rows={soaRows}
                  docs={docs}
                  frameworkId="soc2"
                  loadAll={loadAll}
                  novice={novice}
                  insightsMap={insightsMap}
                  noun="criterion"
                  summaryLabel="Criteria compliant"
                  statusLabel={reportType === 1 ? "Design suitability" : "Operating effectiveness"}
                  groupLabel="All criteria series"
                />
              )}
            </>
          )}
        </div>
      )}

      {/* ── Scoping and report structure ─────────────────────────────────── */}
      {activeSection === "process" && (
        <div>
          <h2 style={heading}>Five-step scoping and assessment process</h2>
          <ol style={{ listStyle: "none", margin: "0 0 24px", padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
            {RISK_STEPS.map((s, i) => (
              <li key={s.step} style={{ ...card, display: "flex", gap: 14, alignItems: "flex-start" }}>
                <span aria-hidden="true" style={{ width: 28, height: 28, borderRadius: "50%", background: theme.color.accentBg, display: "flex", alignItems: "center", justifyContent: "center", ...text.body, fontWeight: 800, color: theme.color.accentText, flexShrink: 0 }}>{i + 1}</span>
                <div>
                  <div style={{ ...text.body, fontWeight: 700, color: theme.color.heading, marginBottom: 3 }}>{s.title}</div>
                  <div style={text.hint}>{s.desc}</div>
                </div>
              </li>
            ))}
          </ol>

          <h2 style={heading}>SOC 2 report structure: four sections</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 10, marginBottom: 24 }}>
            {REPORT_SECTIONS.map((s) => (
              <div key={s.num} style={accentCard}>
                <div style={{ ...text.eyebrow, marginBottom: 4 }}>Section {s.num}</div>
                <div style={{ ...text.body, fontWeight: 700, color: theme.color.heading, marginBottom: 6 }}>{s.title}</div>
                <div style={text.hint}>{s.desc}</div>
              </div>
            ))}
          </div>

          <h2 style={heading}>Criteria functional areas (control design framework)</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
            {FUNCTIONAL_AREAS.map((a) => (
              <div key={a.area} style={accentCard}>
                <div style={{ ...text.body, fontWeight: 700, color: theme.color.heading, marginBottom: 6 }}>{a.area}</div>
                <div style={text.meta}>{a.desc}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Common audit gaps ────────────────────────────────────────────── */}
      {activeSection === "gaps" && (
        <div>
          <div style={{ ...accentCard, marginBottom: 20 }}>
            <div style={{ ...text.body, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>About audit evidence gaps</div>
            <div style={text.hint}>
              These are industry-observed patterns from SOC 2 audit practice, not normative requirements from the AICPA. They represent the areas where organisations most commonly fail to produce adequate evidence during audit, resulting in deviations reported in Section 3 of the report or, in material cases, a qualified opinion in Section 4.
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {EVIDENCE_GAPS.map((g) => (
              <div key={g.area} style={{ ...card, display: "flex", gap: 12, alignItems: "flex-start" }}>
                <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: "50%", background: theme.color.danger, marginTop: 5, flexShrink: 0 }} />
                <div>
                  <div style={{ ...text.ref, fontWeight: 700, color: theme.color.heading, marginBottom: 3 }}>{g.area}</div>
                  <div style={text.hint}>{g.gap}</div>
                </div>
              </div>
            ))}
          </div>

          <h2 style={{ ...heading, marginTop: 24 }}>Deviation types</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 10 }}>
            {DEVIATION_TYPES.map((d) => (
              <div key={d.name} style={accentCard}>
                <div style={{ ...text.body, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>{d.name}</div>
                <div style={{ ...text.eyebrow, color: theme.color.faint, marginBottom: 6 }}>Applies to: {d.scope}</div>
                <div style={text.hint}>{d.desc}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
